import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowLeft, Building2, Users, Truck, Shield, RefreshCw, Search, ChevronDown, ChevronRight, ChevronUp, Zap, Coffee, Clock, AlertTriangle, Filter, ArrowUpDown } from 'lucide-react';
import { api } from '../../utils/api';
import DriverComplianceDetail from '../shared/DriverComplianceDetail';
import DriverProfileTabs from '../shared/DriverProfileTabs';

const ROLE_COLORS = {
  SYSTEM_ADMIN: 'bg-red-500/20 text-red-400',
  CLIENT_ADMIN: 'bg-blue-500/20 text-blue-400',
  SCHEDULER: 'bg-cyan-500/20 text-cyan-400',
  RECORD_KEEPER: 'bg-amber-500/20 text-amber-400',
  DRIVER: 'bg-emerald-500/20 text-emerald-400',
  OWNER_DRIVER: 'bg-emerald-500/20 text-emerald-400',
  GROUP_VIEWER: 'bg-violet-500/20 text-violet-400',
  VIEWER: 'bg-slate-500/20 text-slate-400',
};

const STATE_DOTS = {
  WORK: { color: 'bg-red-500', label: 'Working' },
  REST: { color: 'bg-emerald-500', label: 'Resting' },
  OFF: { color: 'bg-slate-500', label: 'Off Duty' },
  NO_DIARY: { color: 'bg-slate-600', label: 'No Diary' },
  UNAVAILABLE: { color: 'bg-slate-600', label: 'N/A' },
  UNKNOWN: { color: 'bg-slate-600', label: 'Unknown' },
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '0h 0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
};

const FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'WORK', label: 'Working' },
  { id: 'REST', label: 'Resting' },
  { id: 'OFF', label: 'Off Duty' },
];

const SortHeader = ({ label, field, current, dir, onClick, className = '' }) => {
  const active = current === field;
  return (
    <th
      className={`text-left p-3 text-slate-400 font-medium cursor-pointer select-none hover:text-white transition ${className}`}
      onClick={() => onClick(field)}
      data-testid={`sort-${field}`}
    >
      <div className="flex items-center gap-1">
        <span className={active ? 'text-blue-400' : ''}>{label}</span>
        {active ? (
          dir === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-400" /> : <ChevronDown className="w-3 h-3 text-blue-400" />
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-40" />
        )}
      </div>
    </th>
  );
};

const ClientDetailPanel = ({ clientId, clientName, onBack }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [driverStates, setDriverStates] = useState({});
  const [statesLoading, setStatesLoading] = useState(false);
  const [expandedDriver, setExpandedDriver] = useState(null);
  const [fullDriverStates, setFullDriverStates] = useState({});
  const [fullStateLoading, setFullStateLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [sortField, setSortField] = useState('hours_avail');
  const [sortDir, setSortDir] = useState('asc');

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api(`/sysadmin/clients/${clientId}`);
      setDetail(data);
    } catch (err) {
      console.error('Failed to load client detail:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const loadDriverStates = useCallback(async () => {
    setStatesLoading(true);
    try {
      const data = await api(`/sysadmin/clients/${clientId}/driver-states`);
      setDriverStates(data.states || {});
    } catch (err) {
      console.error('Failed to load driver states:', err);
    } finally {
      setStatesLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // Load driver states when switching to drivers tab
  useEffect(() => {
    if (activeSection === 'drivers' && detail?.drivers?.length > 0) {
      loadDriverStates();
    }
  }, [activeSection, detail, loadDriverStates]);

  const loadFullDriverState = useCallback(async (driverId) => {
    if (fullDriverStates[driverId]) return; // already cached
    setFullStateLoading(driverId);
    try {
      const data = await api(`/drivers/${driverId}/state`);
      setFullDriverStates(prev => ({ ...prev, [driverId]: data }));
    } catch (err) {
      console.error('Failed to load full driver state:', err);
    } finally {
      setFullStateLoading(null);
    }
  }, [fullDriverStates]);

  const toggleDriverExpand = useCallback((driverId) => {
    if (expandedDriver === driverId) {
      setExpandedDriver(null);
    } else {
      setExpandedDriver(driverId);
      loadFullDriverState(driverId);
    }
  }, [expandedDriver, loadFullDriverState]);

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Filtered + sorted drivers list
  const filteredDrivers = useMemo(() => {
    if (!detail?.drivers) return [];
    let list = detail.drivers;

    // Text search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(d =>
        `${d.first_name} ${d.last_name}`.toLowerCase().includes(q) ||
        (d.email || '').toLowerCase().includes(q) ||
        (d.license_number || '').toLowerCase().includes(q)
      );
    }

    // State filter
    if (stateFilter !== 'all') {
      list = list.filter(d => {
        const state = driverStates[d.driver_id];
        return state?.current_state === stateFilter;
      });
    }

    // Sort
    list = [...list].sort((a, b) => {
      const sa = driverStates[a.driver_id] || {};
      const sb = driverStates[b.driver_id] || {};
      let va, vb;

      switch (sortField) {
        case 'hours_avail':
          va = sa.hours_available_sec ?? 999999;
          vb = sb.hours_available_sec ?? 999999;
          break;
        case 'must_finish':
          va = sa.must_finish_by ? new Date(sa.must_finish_by).getTime() : (sortDir === 'asc' ? Infinity : -Infinity);
          vb = sb.must_finish_by ? new Date(sb.must_finish_by).getTime() : (sortDir === 'asc' ? Infinity : -Infinity);
          break;
        case 'can_start':
          // null = ready now = sort first in asc
          va = sa.can_start_at ? new Date(sa.can_start_at).getTime() : (sortDir === 'asc' ? -Infinity : Infinity);
          vb = sb.can_start_at ? new Date(sb.can_start_at).getTime() : (sortDir === 'asc' ? -Infinity : Infinity);
          break;
        case 'work_24h':
          va = sa.work_24h_seconds ?? 0;
          vb = sb.work_24h_seconds ?? 0;
          break;
        default:
          va = sa.hours_available_sec ?? 999999;
          vb = sb.hours_available_sec ?? 999999;
      }

      return sortDir === 'asc' ? va - vb : vb - va;
    });

    return list;
  }, [detail?.drivers, searchQuery, stateFilter, driverStates, sortField, sortDir]);

  // State counts for filter badges
  const stateCounts = useMemo(() => {
    const counts = { all: detail?.drivers?.length || 0, WORK: 0, REST: 0, OFF: 0 };
    Object.values(driverStates).forEach(s => {
      if (s.current_state === 'WORK') counts.WORK++;
      else if (s.current_state === 'REST') counts.REST++;
      else counts.OFF++;
    });
    return counts;
  }, [driverStates, detail?.drivers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="client-detail-loading">
        <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="text-center py-16 text-slate-400" data-testid="client-detail-error">
        Failed to load client details.
        <button onClick={onBack} className="block mx-auto mt-4 text-blue-400 hover:text-blue-300">Go back</button>
      </div>
    );
  }

  const { client, drivers, vehicles, users } = detail;
  const sections = [
    { id: 'overview', label: 'Overview', icon: Building2 },
    { id: 'users', label: `Users (${users.length})`, icon: Users },
    { id: 'drivers', label: `Drivers (${drivers.length})`, icon: Shield },
    { id: 'vehicles', label: `Vehicles (${vehicles.length})`, icon: Truck },
  ];

  return (
    <div data-testid="client-detail-panel">
      {/* Back + Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition"
            data-testid="client-detail-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-lg font-semibold text-white">{client.name}</h2>
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <span className={`px-2 py-0.5 rounded-full font-medium ${
                client.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'
              }`}>{client.status}</span>
              {client.abn && <span>ABN: {client.abn}</span>}
              <span>Created {formatDate(client.created_at)}</span>
            </div>
          </div>
        </div>
        <button onClick={() => { loadDetail(); if (activeSection === 'drivers') loadDriverStates(); }} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition" data-testid="client-detail-refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {sections.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
              activeSection === s.id
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'bg-slate-800/50 text-slate-400 border border-slate-700 hover:text-white hover:bg-slate-700/50'
            }`}
            data-testid={`client-section-${s.id}`}
          >
            <s.icon className="w-4 h-4" />
            {s.label}
          </button>
        ))}
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="client-overview-section">
          <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-emerald-500/15 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-slate-400 text-sm">Drivers</span>
            </div>
            <div className="text-3xl font-bold text-white">{drivers.length}</div>
          </div>
          <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-500/15 rounded-xl flex items-center justify-center">
                <Truck className="w-5 h-5 text-amber-400" />
              </div>
              <span className="text-slate-400 text-sm">Vehicles</span>
            </div>
            <div className="text-3xl font-bold text-white">{vehicles.length}</div>
          </div>
          <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-violet-500/15 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-violet-400" />
              </div>
              <span className="text-slate-400 text-sm">Users</span>
            </div>
            <div className="text-3xl font-bold text-white">{users.length}</div>
          </div>
        </div>
      )}

      {/* Users Section */}
      {activeSection === 'users' && (
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden" data-testid="client-users-section">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-4 text-slate-400 font-medium">User</th>
                <th className="text-left p-4 text-slate-400 font-medium">Role(s)</th>
                <th className="text-left p-4 text-slate-400 font-medium hidden sm:table-cell">Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-b border-slate-700/50 hover:bg-slate-700/20 transition">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-medium">
                          {(u.first_name?.[0] || '').toUpperCase()}{(u.last_name?.[0] || '').toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="text-white font-medium">{u.first_name} {u.last_name}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      {(u.roles || []).map(r => (
                        <span key={r} className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[r] || 'bg-slate-600/20 text-slate-400'}`}>
                          {r.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-4 hidden sm:table-cell">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      u.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'
                    }`}>{u.status}</span>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={3} className="p-8 text-center text-slate-500">No users in this client</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Drivers Section - Enhanced with live states */}
      {activeSection === 'drivers' && (
        <div data-testid="client-drivers-section">
          {/* Search + Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search drivers..."
                className="w-full pl-9 pr-4 py-2 bg-slate-800/60 border border-slate-700 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50"
                data-testid="driver-search-input"
              />
            </div>
            <div className="flex gap-1.5">
              {FILTER_OPTIONS.map(f => (
                <button
                  key={f.id}
                  onClick={() => setStateFilter(f.id)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition ${
                    stateFilter === f.id
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : 'bg-slate-800/50 text-slate-400 border border-slate-700 hover:text-white'
                  }`}
                  data-testid={`driver-filter-${f.id}`}
                >
                  {f.label}
                  {stateCounts[f.id] > 0 && (
                    <span className="ml-1.5 text-[10px] opacity-70">({stateCounts[f.id]})</span>
                  )}
                </button>
              ))}
              <button
                onClick={loadDriverStates}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 border border-slate-700 transition"
                title="Refresh states"
                data-testid="driver-states-refresh"
              >
                <RefreshCw className={`w-4 h-4 ${statesLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Drivers Table */}
          <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="w-8 p-3"></th>
                  <th className="text-left p-3 text-slate-400 font-medium">Driver</th>
                  <th className="text-left p-3 text-slate-400 font-medium">Live Status</th>
                  <SortHeader label="Hours Avail" field="hours_avail" current={sortField} dir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Must Finish" field="must_finish" current={sortField} dir={sortDir} onClick={toggleSort} className="hidden md:table-cell" />
                  <SortHeader label="Can Start" field="can_start" current={sortField} dir={sortDir} onClick={toggleSort} className="hidden md:table-cell" />
                  <SortHeader label="24h Work" field="work_24h" current={sortField} dir={sortDir} onClick={toggleSort} className="hidden lg:table-cell" />
                  <th className="text-left p-3 text-slate-400 font-medium hidden sm:table-cell">Compliance</th>
                </tr>
              </thead>
              <tbody>
                {filteredDrivers.map(d => {
                  const state = driverStates[d.driver_id];
                  const dot = STATE_DOTS[state?.current_state] || STATE_DOTS.UNKNOWN;
                  const isExpanded = expandedDriver === d.driver_id;

                  return (
                    <React.Fragment key={d.driver_id}>
                      <tr
                        className={`border-b border-slate-700/50 cursor-pointer transition ${
                          isExpanded ? 'bg-slate-700/30' : 'hover:bg-slate-700/20'
                        }`}
                        onClick={() => toggleDriverExpand(d.driver_id)}
                        data-testid={`driver-row-${d.driver_id}`}
                      >
                        <td className="p-3 text-slate-500">
                          {isExpanded
                            ? <ChevronDown className="w-4 h-4" />
                            : <ChevronRight className="w-4 h-4" />
                          }
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="relative">
                              <div className="w-7 h-7 bg-emerald-500/15 rounded-full flex items-center justify-center flex-shrink-0">
                                <Shield className="w-3.5 h-3.5 text-emerald-400" />
                              </div>
                              {state && (
                                <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 ${dot.color} rounded-full border-2 border-slate-800`} title={dot.label} />
                              )}
                            </div>
                            <div>
                              <div className="text-white font-medium text-xs">{d.first_name} {d.last_name}</div>
                              <div className="text-[10px] text-slate-500">{d.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          {statesLoading ? (
                            <div className="w-3.5 h-3.5 border border-slate-600 border-t-blue-400 rounded-full animate-spin" />
                          ) : state ? (
                            <div className="flex items-center gap-1.5">
                              <div className={`w-2 h-2 rounded-full ${dot.color}`} />
                              <span className="text-xs text-slate-300">{dot.label}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-600">-</span>
                          )}
                        </td>
                        {/* Hours Available */}
                        <td className="p-3">
                          {state ? (
                            <span className={`text-xs font-mono font-medium ${
                              (state.hours_available_sec || 0) < 7200 ? 'text-red-400' :
                              (state.hours_available_sec || 0) < 14400 ? 'text-amber-400' :
                              'text-emerald-400'
                            }`}>
                              {formatDuration(state.hours_available_sec)}
                            </span>
                          ) : <span className="text-xs text-slate-600">-</span>}
                        </td>
                        {/* Must Finish By */}
                        <td className="p-3 hidden md:table-cell">
                          {state?.must_finish_by ? (
                            <div>
                              <span className="text-xs font-mono text-amber-400">
                                {new Date(state.must_finish_by).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true })}
                              </span>
                              <div className="text-[9px] text-slate-500">{state.must_finish_reason}</div>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-600">-</span>
                          )}
                        </td>
                        {/* Can Start At */}
                        <td className="p-3 hidden md:table-cell">
                          {state?.current_state === 'REST' ? (
                            state.can_start_at ? (
                              <span className="text-xs font-mono text-blue-400">
                                {new Date(state.can_start_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true })}
                              </span>
                            ) : (
                              <span className="text-xs font-medium text-emerald-400">Ready</span>
                            )
                          ) : (
                            <span className="text-xs text-slate-600">-</span>
                          )}
                        </td>
                        {/* 24h Work */}
                        <td className="p-3 hidden lg:table-cell">
                          {state ? (
                            <span className="text-xs text-slate-300 font-mono">{formatDuration(state.work_24h_seconds)}</span>
                          ) : (
                            <span className="text-xs text-slate-600">-</span>
                          )}
                        </td>
                        {/* Compliance */}
                        <td className="p-3 hidden sm:table-cell">
                          {state?.compliance_status ? (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              state.compliance_status === 'CRITICAL' ? 'bg-red-500/20 text-red-400' :
                              state.compliance_status === 'WARNING' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-emerald-500/20 text-emerald-400'
                            }`}>
                              {state.compliance_status === 'CRITICAL' && <AlertTriangle className="w-3 h-3 inline mr-0.5" />}
                              {state.compliance_status}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-600">-</span>
                          )}
                        </td>
                      </tr>
                      {/* Expanded Profile Tabs */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} className="p-0">
                            <div className="px-6 py-4 bg-slate-800/60 border-b border-slate-700/50">
                              {fullStateLoading === d.driver_id ? (
                                <div className="flex items-center gap-2 py-4">
                                  <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                                  <span className="text-sm text-slate-400">Loading driver data...</span>
                                </div>
                              ) : (
                                <DriverProfileTabs
                                  driverId={d.driver_id}
                                  driverName={`${d.first_name} ${d.last_name}`}
                                  complianceState={fullDriverStates[d.driver_id]}
                                  canVerify={true}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {filteredDrivers.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-slate-500">
                      {searchQuery || stateFilter !== 'all' ? 'No drivers match your filter' : 'No drivers in this client'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Vehicles Section */}
      {activeSection === 'vehicles' && (
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden" data-testid="client-vehicles-section">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-4 text-slate-400 font-medium">Registration</th>
                <th className="text-left p-4 text-slate-400 font-medium hidden sm:table-cell">Make / Model</th>
                <th className="text-left p-4 text-slate-400 font-medium hidden md:table-cell">GVM (kg)</th>
                <th className="text-left p-4 text-slate-400 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map(v => (
                <tr key={v.vehicle_id} className="border-b border-slate-700/50 hover:bg-slate-700/20 transition">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-amber-500/15 rounded-full flex items-center justify-center flex-shrink-0">
                        <Truck className="w-4 h-4 text-amber-400" />
                      </div>
                      <span className="text-white font-medium">{v.registration}</span>
                    </div>
                  </td>
                  <td className="p-4 hidden sm:table-cell text-slate-300">{v.make} {v.model}</td>
                  <td className="p-4 hidden md:table-cell text-slate-400">{v.gvm_kg ? `${v.gvm_kg.toLocaleString()} kg` : '-'}</td>
                  <td className="p-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      v.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'
                    }`}>{v.status}</span>
                  </td>
                </tr>
              ))}
              {vehicles.length === 0 && (
                <tr><td colSpan={4} className="p-8 text-center text-slate-500">No vehicles in this client</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClientDetailPanel;
